import { inject, observer } from 'mobx-react';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Close } from '@mui/icons-material';
import { Button, IconButton, Tooltip, Typography } from '@mui/material';
import Box from '@mui/material/Box';

import isomorphicLocalStorage from 'main/modules/isomorphic-local-storage';

import SearchTemplates from '../search/Templates';

/**
 * Checking the bulletin type and decide possible user segment to show one of possible "call to action"
 * @param item {Bulletin}
 */

const useStyles = makeStyles()((theme) => ({
    segment: {
        padding: 60,
        borderBottom: '1px solid #E0E0E0'
    },
    segmentCloseButton: {
        float: 'right'
    },
    content: {
        padding: 0,
        borderRadius: 0
    },
    actionButton: {
        marginTop: 16,
        marginBottom: 16
    },
    link: {
        color: theme.palette.text.primary
    },
    flex: {
        display: 'flex',
        flexWrap: 'wrap'
    }
}));

const offensive = ['exploit', 'bugbounty', 'scanner', 'tools'];
const news = ['info', 'blog'];

const Banner = ({ children, name }) => {
    const { classes } = useStyles();
    const [hidden, setHidden] = useState(isomorphicLocalStorage.getItem(name) === 'hidden');
    const hide = () => {
        isomorphicLocalStorage.setItem(name, 'hidden');
        setHidden(true);
    };

    return (
        !hidden && (
            <Box className={classes.segment}>
                <Tooltip title="Do not show again" className={classes.segmentCloseButton}>
                    <IconButton color="primary" onClick={hide} size="large">
                        <Close />
                    </IconButton>
                </Tooltip>
                {children}
            </Box>
        )
    );
};

const LinuxScanner = ({ classes }) => (
    <Banner name="segmentLinuxScanner">
        <Box className={classes.content}>
            <Typography variant="h5">How to protect your server from attacks?</Typography>

            <br />

            <div style={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                <Typography variant="body1">
                    Get pack of different security solutions such as
                    <ul>
                        <li>
                            {' '}
                            <a className={classes.link} target="_blank" href="/scan-help">
                                Linux agent scanner
                            </a>
                        </li>
                        <li>
                            {' '}
                            <a
                                className={classes.link}
                                target="_blank"
                                href="https://github.com/vulnersCom/zabbix-threat-control"
                                rel="noreferrer"
                            >
                                Zabbix Plugin
                            </a>
                        </li>
                        <li>
                            {' '}
                            <a
                                className={classes.link}
                                target="_blank"
                                href="https://github.com/vulnersCom/splunk-vulners"
                                rel="noreferrer"
                            >
                                Splunk Plugin
                            </a>
                        </li>
                        <li>
                            {' '}
                            <a className={classes.link} target="_blank" href="/apiscan-help">
                                Open API Integration
                            </a>
                        </li>
                    </ul>
                    {/* TODO Get items from PRODUCTS TYPES.DEFENSIVE*/}
                    {/*<div className="nav-container">*/}
                    {/*    {PRODUCTS.filter(i => i.type === TYPES.DEFENSIVE).map(props =>*/}
                    {/*        <ProductCard {...props.card} key={props.name} onClick={() => {*/}
                    {/*            console.log('[PRODUCT]')*/}
                    {/*        }} />*/}
                    {/*    )}*/}
                    {/*</div>*/}
                </Typography>

                <Button href="/linux-scanner/scan" className={classes.actionButton} variant="contained" color="primary">
                    Get Linux Scanner
                </Button>
            </div>
        </Box>
    </Banner>
);

const API = ({ classes }) => (
    <Banner name="segmentAPI">
        <Box className={classes.content}>
            <Typography variant="h5">Use Vulners API to create your own security tool</Typography>

            <br />

            <div className={classes.flex} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1">
                    <div>API usage cases</div>
                    <ul>
                        <li>Network scanning</li>
                        <li>Linux Patch management</li>
                        <li>Threat protection</li>
                        <li>No network audit solution</li>
                    </ul>
                </Typography>
                <Typography variant="body1">
                    <div>Ways of integration</div>
                    <ul>
                        <li>
                            {' '}
                            <a
                                className={classes.link}
                                target="_blank"
                                href="https://github.com/vulnersCom/api"
                                rel="noreferrer"
                            >
                                Python SDK
                            </a>
                        </li>
                        <li>
                            {' '}
                            <a className={classes.link} target="_blank" href="/docs">
                                Rest API
                            </a>
                        </li>
                        <li>
                            {' '}
                            <a className={classes.link} href="/apiscan-help/#integration">
                                Bash one-liner
                            </a>
                        </li>
                    </ul>
                </Typography>
            </div>

            <Button href="/apiscan-help" className={classes.actionButton} variant="contained" color="primary">
                Integrate Vulners API
            </Button>
        </Box>
    </Banner>
);

const NetworkScanner = ({ classes, item }) => (
    <Banner name="segmentNetworkScanner">
        <Box className={[classes.content, classes.flex].join(' ')}>
            <div>
                <Typography variant="h5" component="div">
                    {item.bulletinFamily === 'bugbounty'
                        ? 'How to be winner in bugbounty?'
                        : 'How to find holes in your network?'}
                </Typography>
                <br />
                <Typography variant="body1">
                    Try incredible fast Vulners Perimeter Scanner and find vulnerabilities and unnecessary ip and ports
                    in network devices inside your network before anyone else.
                </Typography>
            </div>
            <Button href="/vscanner" className={classes.actionButton} variant="contained" color="primary">
                Try Network Scanner
            </Button>
        </Box>
    </Banner>
);

const Subscriptions = ({ classes }) => (
    <Banner name="segmentSubscriptions">
        <Box className={classes.content}>
            <Typography variant="h5">Be first who know about 0-days in popular software</Typography>
            <Typography variant="body1">
                Do not waste time on finding information in tons of articles. Subscribe yourself and your colleagues on
                news and articles about products you need and you use!
            </Typography>
            <SearchTemplates />
            <Button href="/subscriptions" className={classes.actionButton} variant="contained" color="primary">
                Subscribe on news
            </Button>
        </Box>
    </Banner>
);

const DISPLAY_RATE = 0.5;

const ItemSegment = ({ item, userStore }) => {
    const { classes } = useStyles();

    if (userStore.isLoading || userStore.hasLicense) {
        return false;
    }

    const isAuthorized = userStore.user;
    const props = { isAuthorized, classes, item };
    if (offensive.indexOf(item.bulletinFamily) >= 0) {
        return <NetworkScanner {...props} />;
    } else if (news.indexOf(item.bulletinFamily) >= 0) {
        return <Subscriptions {...props} />;
    }

    return Math.random() > DISPLAY_RATE ? <API {...props} /> : <LinuxScanner {...props} />;
};

export default inject('userStore')(observer(ItemSegment));
